import type { UIAction, ActionResult } from '../types'

const ACTION_TIMEOUT_MS = 5000
const SETTLE_DELAY_MS = 100

/**
 * Sequential action execution engine.
 * Ensures actions run one at a time with state settlement between them.
 */
export function createActionDispatcher() {
    let isExecuting = false
    const queue: Array<{
        action: UIAction
        params?: Record<string, any>
        resolve: (result: ActionResult) => void
    }> = []

    async function processQueue(): Promise<void> {
        if (isExecuting) return
        isExecuting = true

        while (queue.length > 0) {
            const item = queue.shift()!
            try {
                const result = await Promise.race([
                    item.action.handler(item.params),
                    new Promise<ActionResult>((_, reject) =>
                        setTimeout(() => reject(new Error('Action timed out')), ACTION_TIMEOUT_MS)
                    ),
                ])
                item.resolve(result)
            } catch (error: any) {
                console.error(`[ELARA:Dispatcher] ❌ Error for ${item.action.actionId}:`, error)
                item.resolve({
                    success: false,
                    message: error.message || 'Action execution failed',
                })
            }

            // Wait for state to settle before next action
            await new Promise((resolve) => setTimeout(resolve, SETTLE_DELAY_MS))
        }

        isExecuting = false
    }

    function execute(action: UIAction, params?: Record<string, any>): Promise<ActionResult> {
        return new Promise((resolve) => {
            queue.push({ action, params, resolve })
            processQueue()
        })
    }

    return { execute }
}
